import { globalErrorResponse } from '../notifications/actions';
import { productlistConfig } from '../../config';
import RoutingUtils from '../../shared/utils/utils-routing';

import * as types from '../constants';
import { SearchScope } from './state';
import { CatalogueSearchScope, CollectionSearchScope, SearchSuggestionResponseOut } from '../api';
import {
    SEARCH_QUERY_SELECTED_TOPHIT,
    SEARCH_SUGGESTIONS,
    SEARCH_SUGGESTIONS_RESET,
    SearchSuggestions,
    SearchSuggestionsReset
} from './types';
import { AppDispatch, GetAppState } from '../index';
import { matchPath } from 'react-router';
import { AppState } from '../appState';
import { http } from '../../api/axios';

const SCOPE_LANGUAGELEARNING = 'LANGUAGE_LEARNING';
const SCOPE_LANGUAGELEARNING_REQUESTKEY = 'LANGUAGE_LEARNING';
const SCOPE_LANGUAGELEARNING_NAME = 'Language Learning';

const SCOPE_CATALOGUE = 'catalogue';
const SCOPE_CATALOGUE_REQUESTKEY = 'CATALOGUE';
const SCOPE_CATALOGUE_NAME = 'Catalogue';

export const SCOPE_CATALOGUE_OBJECT: SearchScope = {
    requestKey: SCOPE_CATALOGUE_REQUESTKEY,
    key: SCOPE_CATALOGUE,
    name: SCOPE_CATALOGUE_NAME
};

const SCOPE_LOTE_KEY = 'LOTE';
const SCOPE_LOTE_NAME = 'LOTE';
const SCOPE_LOTE_REQUESTKEY = 'LOTE';

export const SCOPE_LOTE_OBJECT: SearchScope = {
    requestKey: SCOPE_LOTE_REQUESTKEY,
    key: SCOPE_LOTE_KEY,
    name: SCOPE_LOTE_NAME
};

export const SCOPE_LANGUAGELEARNING_OBJECT: SearchScope = {
    requestKey: SCOPE_LANGUAGELEARNING_REQUESTKEY,
    key: SCOPE_LANGUAGELEARNING,
    name: SCOPE_LANGUAGELEARNING_NAME
};

const SCOPE_ALL_REQUESTKEY = 'ALL';
const SCOPE_ALL_KEY = 'ALL';
const SCOPE_ALL_NAME = 'All Formats';
export const SCOPE_ALL_OBJECT: SearchScope = {
    requestKey: SCOPE_ALL_REQUESTKEY,
    key: SCOPE_ALL_KEY,
    name: SCOPE_ALL_NAME
};
const SCOPE_EBOOK_REQUESTKEY = 'EBOOKS';
const SCOPE_EBOOK_KEY = 'EBOOKS';
const SCOPE_EBOOK_NAME = 'eBooks';
export const SCOPE_EBOOK_OBJECT: SearchScope = {
    requestKey: SCOPE_EBOOK_REQUESTKEY,
    key: SCOPE_EBOOK_KEY,
    name: SCOPE_EBOOK_NAME
};
const SCOPE_EAUDIOBOOKS_REQUESTKEY = 'EAUDIOBOOKS';
const SCOPE_EAUDIOBOOKS_KEY = 'EAUDIOBOOKS';
const SCOPE_EAUDIOBOOKS_NAME = 'eAudiobooks';
export const SCOPE_EAUDIOBOOK_OBJECT: SearchScope = {
    requestKey: SCOPE_EAUDIOBOOKS_REQUESTKEY,
    key: SCOPE_EAUDIOBOOKS_KEY,
    name: SCOPE_EAUDIOBOOKS_NAME
};

const SCOPE_EPRESS_REQUESTKEY = 'EPRESS';
const SCOPE_EPRESS_KEY = 'EPRESS';
const SCOPE_EPRESS_NAME = 'ePress';

export const SCOPE_EPRESS_OBJECT: SearchScope = {
    requestKey: SCOPE_EPRESS_REQUESTKEY,
    key: SCOPE_EPRESS_KEY,
    name: SCOPE_EPRESS_NAME
};

export type SearchSuggestionPayload = {
    data: SearchSuggestionResponseOut;
    loanFormat?: string;
};

export function mapLanguageScopeKeyToCamelCase(searchScope: SearchScope) {
    switch (searchScope.key) {
        case SCOPE_LANGUAGELEARNING_OBJECT.key:
            return 'languageLearning';
        case SCOPE_LOTE_OBJECT.key:
            return 'lote';
        default:
            return null;
    }
}

export function getCatalogueSearchScopes(isMultiLanguageShopEnabled: boolean) {
    if (isMultiLanguageShopEnabled) {
        return [
            SCOPE_EAUDIOBOOK_OBJECT,
            SCOPE_EBOOK_OBJECT,
            SCOPE_EPRESS_OBJECT,
            SCOPE_LANGUAGELEARNING_OBJECT,
            SCOPE_CATALOGUE_OBJECT
        ];
    }

    return [
        SCOPE_EAUDIOBOOK_OBJECT,
        SCOPE_EBOOK_OBJECT,
        SCOPE_EPRESS_OBJECT,
        SCOPE_LOTE_OBJECT,
        SCOPE_LANGUAGELEARNING_OBJECT,
        SCOPE_CATALOGUE_OBJECT
    ];
}

export function getYourBorrowBoxSearchScopes() {
    return [SCOPE_EAUDIOBOOK_OBJECT, SCOPE_EBOOK_OBJECT, SCOPE_EPRESS_OBJECT, SCOPE_ALL_OBJECT];
}

export function triggerSearchPage(query) {
    RoutingUtils.pushUrlWithQuery(types.PATH_SEARCH, query);
}

export const resetSearchSuggestions = (): SearchSuggestionsReset => {
    return { type: SEARCH_SUGGESTIONS_RESET };
};

export function isInYourBorrowBox(state: AppState): boolean {
    return !!matchPath(state.router.location.pathname, {
        path: ['/collection', '/localcontent', '/marketing', '/collectionmgmt', '/merchandising']
    });
}

export function triggerSearchSuggestions(query: string, scope?: SearchScope, shopLanguage?: string) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        if (!query) {
            return dispatch(resetSearchSuggestions());
        }

        if (isInYourBorrowBox(getState())) {
            //TODO: no search suggestions endpoint for Your BorrowBox implemented yet
            return dispatch(resetSearchSuggestions());
        }

        if (scope && scope.key) {
            // FIXME the scope key is not a loan format... WTF?
            const loanFormat = scope.key.toUpperCase();

            http.get(productlistConfig().endpointSearchSuggestionsLoanFormat, {
                params: {
                    q: query,
                    pageSize: 5,
                    shopLanguage
                },
                pathParams: {
                    kind: 'products',
                    loanFormat
                }
            })
                .then(response => {
                    dispatch(searchSuggestionsSuccess(response.data, loanFormat));
                })
                .catch(response => {
                    if (!response.runtimeDuplication) {
                        dispatch(globalErrorResponse(response));
                    } else {
                        dispatch(searchSuggestionsSuccess(response.data, loanFormat));
                    }
                });
        } else {
            http.get(productlistConfig().endpointSearchSuggestions, {
                params: {
                    q: query,
                    pageSize: 5,
                    shopLanguage
                }
            })
                .then(response => {
                    dispatch(searchSuggestionsSuccess(response.data));
                })
                .catch(response => {
                    if (!response.runtimeDuplication) {
                        dispatch(globalErrorResponse(response));
                    } else {
                        dispatch(searchSuggestionsSuccess(response.data));
                    }
                });
        }
    };
}

export function setTopHitLabel(topHitLabel: string) {
    return (dispatch: AppDispatch) => {
        dispatch({
            type: SEARCH_QUERY_SELECTED_TOPHIT,
            payload: topHitLabel
        });
    };
}

function searchSuggestionsSuccess(data: SearchSuggestionResponseOut, loanFormat?: string): SearchSuggestions {
    return {
        type: SEARCH_SUGGESTIONS,
        payload: {
            data,
            loanFormat
        }
    };
}

export const mapCollectionSearchScope = (scope: string): CollectionSearchScope => {
    switch (scope.toUpperCase()) {
        case 'EBOOKS':
            return CollectionSearchScope.EBOOKS;
        case 'EAUDIOBOOKS':
            return CollectionSearchScope.EAUDIOBOOKS;
        case 'EMAGAZINES':
        case 'EPRESS':
            return CollectionSearchScope.EPRESS;
        case 'ALL':
        default:
            return CollectionSearchScope.ALL;
    }
};

export const mapCatalogueSearchScope = (scope: string): CatalogueSearchScope => {
    switch (scope.toUpperCase()) {
        case 'EBOOKS':
            return CatalogueSearchScope.EBOOKS;
        case 'EAUDIOBOOKS':
            return CatalogueSearchScope.EAUDIOBOOKS;
        case 'LOTE':
            return CatalogueSearchScope.LOTE;
        case 'LANGUAGE_LEARNING':
            return CatalogueSearchScope.LANGUAGE_LEARNING;
        case 'EPRESS':
        case 'EMAGAZINES':
            return CatalogueSearchScope.EPRESS;
        case 'CATALOGUE':
        default:
            return CatalogueSearchScope.CATALOGUE;
    }
};
